import React, { memo, forwardRef, useState, useImperativeHandle, useEffect, useCallback, useMemo } from 'react';
import { DropDownPicker, Modal, ScrollView, Title, Text } from "@constants/Themed";
import { BlurView, View } from "@constants/Themed";
import { KeyboardAvoidingView, Pressable, FlatList, Platform } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import styles from '@stylesheet';
import toTitleCase from '@hooks/toTitleCase';
import TagInput from '@components/Global/TagInput';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import Checkbox from 'expo-checkbox';
import * as Animatable from 'react-native-animatable';
import Button from '@components/UI/Button/Button';
import apis from '@api/api';
import { H5, XStack, YStack, Image } from 'tamagui';
import departSelectList from '@constants/departSelectList';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
interface UserOptionsModalProps {
  userList?: any[];
  callBack?: (link?: any, shouldRemove?: boolean) => void;
}

export type UserOptionsModalHandle = {
  openModal: (target: string, source: string) => void;
}

const UserOptionsModal = forwardRef<UserOptionsModalHandle, UserOptionsModalProps>((props, ref) => {
  const { userList, callBack } = props;
  const [visible, setVisible] = useState(false);
  const [user, setUser] = useState<any>({});
  const [tags, setTags] = useState<string[]>([]);
  const [department, setDepartment] = useState<string>('');
  const [open, setOpen] = useState(false);
  const theme = themeSelector();
  const [users, setUsers] = useState({
    passedUser: null,
    sourceUser: null
  });
  const [linkedUser, setLinkedUser] = useState(null);
  const [linkedUserValue, setLinkedUserValue] = useState(false);
  const filteredUserList = department ? userList?.filter((user) => user.user?._id === department) || [] : userList;

  const tagHandler = useCallback((tags) => {
    setTags(tags);
  }, [tags])

  useImperativeHandle(ref, () => ({
    openModal: (target, source) => {
      setVisible(true);
      const foundUser = userList.length > 0 ? userList?.filter((user) => user.user?._id == target) : [];
      setUser(foundUser);
      setUsers({ passedUser: target, sourceUser: source });
      // Initialize tags and department from user data if available
      if (foundUser.length > 0) {
        setTags(foundUser[0]?.tags || []);
        setDepartment(foundUser[0]?.department || '');
      }
    }
  }));


  return (
    <Modal
      setVisible={setVisible}
      style={{ paddingVertical: "15%" }}
      containerStyle={{ minHeight: "70vh", paddingVertical: "20%" }}
      extraChildren={(
        <Button
          buttonText={"Link User"}
          customStyle={{
            maxHeight: 48,
            borderRadius: 12,
            backgroundColor: theme ? '#667eea' : '#4a90e2',
            shadowColor: theme ? '#667eea' : '#4a90e2',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 6,
            marginHorizontal: 20,
          }}
          onPress={async () => {
            try {
              // 1. Optimistic update - add link locally immediately
              const optimisticLink = {
                id: `${users.sourceUser}-${users.passedUser}`,
                from: users.sourceUser,
                to: users.passedUser,
                type: 'standard',
                direction: 'bottomLinks',
                isOptimistic: true // Flag to identify optimistic updates
              };

              console.log('🎯 Adding optimistic link:', optimisticLink);

              // Call the callback to add the link locally
              if (callBack) {
                callBack(optimisticLink, false);
              }

              // Close modal immediately for better UX
              setVisible(false);

            // 2. Make API call in background
              await apis.buildLink({
                targetUser: users.passedUser,
                sourceUser: users.sourceUser,
                linkType: 'standard',
                direction: 'bottomLinks'
              });

              console.log('✅ Link created successfully via API');

            } catch (error) {
              console.error('❌ Failed to create link:', error);

              // 3. Revert optimistic update on failure
              const failedLink = {
                id: `${users.sourceUser}-${users.passedUser}`,
                from: users.sourceUser,
                to: users.passedUser,
                type: 'standard',
                direction: 'bottomLinks',
                isOptimistic: true
              };

              console.log('🔄 Reverting optimistic link:', failedLink);
              if (callBack) {
                callBack(failedLink, true); // Pass true to indicate removal
              }

              // Show error to user
              alert('Failed to create link. Please try again.');
            }
          }}
        />
      )}
      isVisible={visible}
      style={{
        overflow: 'visible',
        padding: 0,
        borderRadius: 24,
        backgroundColor: 'transparent'
      }}
    >
      <View style={{
        maxWidth: "110%",
        marginHorizontal: Platform.select({ web: -20, default: -20 }),
        flex: 1,
        overflow: 'visible',
        alignSelf: 'stretch'
      }}>

        {/* Modern glass morphism container */}
        <View style={[
          styles.centerEverything,
          {
            minWidth: Platform.select({ web: 700, default: 400 }),
            flex: 1,
            padding: 32,
            borderRadius: 24,
            gap: 24,
            paddingVertical: 32,
            minHeight: "102%",
            overflow: 'hidden',
            backgroundColor: theme
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(20, 24, 36, 0.95)',
            backdropFilter: 'blur(20px)',
            borderWidth: 1,
            borderColor: theme
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(255, 255, 255, 0.1)',
            shadowColor: theme ? '#000' : '#000',
            shadowOffset: { width: 0, height: 20 },
            shadowOpacity: theme ? 0.1 : 0.3,
            shadowRadius: 40,
            elevation: 20,
          }
        ]}>
          {/* Subtle gradient overlay */}
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 200,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: theme
              ? 'rgba(102, 126, 234, 0.05)'
              : 'rgba(74, 144, 226, 0.1)',
          }} />

          {/* Modern Avatar Section */}
          <YStack style={{
            flex: 1,
            minWidth: "100%",
            alignItems: 'center',
            zIndex: 1
          }}>
            <View style={{
              borderRadius: 24,
              padding: 4,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.1)',
              shadowColor: theme ? '#667eea' : '#4a90e2',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.2,
              shadowRadius: 16,
              elevation: 8,
            }}>
              <AvatarHandler
                avatar={user[0]?.user?.profile_information?.avatar?.img}
                replacementImageStyle={{
                  width: 250,
                  minHeight: 130,
                  borderRadius: 20,
                  backgroundColor: theme ? '#f8f9fa' : '#2a2d3a'
                }}
                avatarImageStyle={{
                  borderRadius: 20,
                  overflow: 'hidden',
                  width: 250,
                  minHeight: 130
                }}
              />
            </View>
          </YStack>

          {/* Modern Name Display */}
          <H5
            numberOfLines={1}
            style={{
              textAlign: "center",
              color: theme ? '#2c3e50' : '#ffffff',
              fontSize: 24,
              fontWeight: '600',
              letterSpacing: 0.5,
              textShadowColor: theme ? 'transparent' : 'rgba(0,0,0,0.3)',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 2,
              zIndex: 1
            }}
          >
            {toTitleCase(user[0]?.user?.profile_information?.name || "") + ' ' + toTitleCase(user[0]?.user?.profile_information?.surname || '')}
          </H5>
          {/* Modern Form Section */}
          <KeyboardAvoidingView
            behavior='padding'
            keyboardVerticalOffset={120}
            style={{
              flex: 2,
              minWidth: '100%',
              gap: 24,
              zIndex: 1,
              minHeight: 600
            }}
          >
            <View style={{ flex: 1, gap: 24 }}>
              {/* Tag Section */}
              <View style={{
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.08)',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                shadowColor: theme ? '#667eea' : '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.1 : 0.2,
                shadowRadius: 8,
                elevation: 4,
              }}>
                <Title style={{
                  color: theme ? '#2c3e50' : '#ffffff',
                  fontSize: 18,
                  fontWeight: '600',
                  marginBottom: 8
                }}>
                  Tag your user
                </Title>
                <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16 }}>
                  <AntDesign
                    name="question-circle"
                    size={16}
                    color={theme ? '#667eea' : '#74B9FF'}
                  />
                  <Text style={{
                    flex: 1,
                    color: theme ? '#64748b' : 'rgba(255, 255, 255, 0.8)',
                    fontStyle: 'italic',
                    fontSize: 14,
                    lineHeight: 20
                  }}>
                    Automatically assign optimum datasets
                  </Text>
                </View>
                <TagInput placeHolder={''} tags={tags} setTags={tagHandler} />
              </View>

              {/* Department Section */}
              <View style={{
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.08)',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                shadowColor: theme ? '#667eea' : '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.1 : 0.2,
                shadowRadius: 8,
                marginBottom: 100,
                elevation: 4,
                zIndex: 10
              }}>
                <Title style={{
                  color: theme ? '#2c3e50' : '#ffffff',
                  fontSize: 18,
                  fontWeight: '600',
                  marginBottom: 8
                }}>
                  Assign a department
                </Title>
                <View style={{ flexDirection: 'row', gap: 10, marginVertical: 12 }}>
                  <AntDesign
                    name="question-circle"
                    size={16}
                    color={theme ? '#667eea' : '#74B9FF'}
                  />
                  <Text style={{
                    flex: 1,
                    color: theme ? '#64748b' : 'rgba(255, 255, 255, 0.8)',
                    fontStyle: 'italic',
                    fontSize: 14,
                    lineHeight: 20
                  }}>
                    Assign a department to your user
                  </Text>
                </View>
                <DropDownPicker
                  open={open}
                  setOpen={setOpen}
                  addCustomItem
                  direction="TOP"
                  items={departSelectList}
                  containerStyle={{
                    height: 48,
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 12,
                    backgroundColor: theme ? '#ffffff' : 'rgba(255, 255, 255, 0.1)',
                    flex: 1,
                    maxWidth: '100%'
                  }}
                  dropDownContainerStyle={{
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 12,
                    backgroundColor: theme ? '#ffffff' : 'rgba(20, 24, 36, 0.95)',
                    marginTop: 8
                  }}
                  dropDownContainerTextStyle={{
                    color: theme ? '#2c3e50' : '#ffffff'
                  }}
                  textStyle={{
                    color: theme ? '#2c3e50' : '#ffffff',
                    fontSize: 16
                  }}
                  placeholderStyle={{
                    color: theme ? '#64748b' : 'rgba(255, 255, 255, 0.6)'
                  }}
                  multiple={false}
                  setValue={setDepartment}
                  value={department}
                />
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </View>
    </Modal>
  )
})

export default UserOptionsModal;